import { errorMessageFrom } from "../../../../../utils/formErrors";
import React, { useEffect, useState } from "react";
import {
  Box, Stack, Dialog, TextField, CircularProgress, IconButton, Checkbox,
  Accordion, AccordionSummary, AccordionDetails, Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShieldOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import EmptyState from "../../../../../component/EmptyState";
import SectionLoader from "../../../../../component/SectionLoader";
import { useNotify } from "../../../../../component/NotificationProvider";
import { getRoles, createRole, updateRole, getPermissionCatalogue } from "../../../../../services/roles";
import { tokens, displayFont, radius } from "../../../../../theme";

const cardSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px` };

const RoleDialog = ({ open, onClose, editing, catalogue, onSaved }) => {
  const notify = useNotify();
  const [form, setForm] = useState({ name: "", description: "" });
  const [perms, setPerms] = useState(new Set());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: editing?.name || "", description: editing?.description || "" });
      setPerms(new Set(editing?.permissions || []));
      setErrors({});
    }
  }, [open, editing]);

  const toggle = (name) => setPerms((p) => {
    const n = new Set(p); n.has(name) ? n.delete(name) : n.add(name); return n;
  });

  const save = async () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.description.trim()) e.description = "Description is required";
    if (perms.size === 0) e.perms = "Select at least one permission";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSaving(true);
    try {
      const payload = { ...form, permissions: [...perms] };
      const res = editing ? await updateRole(editing._id, payload) : await createRole(payload);
      if (res?.success !== false) {
        notify.success(editing ? "Role updated" : "Role created");
        onSaved();
        onClose();
      } else notify.error(errorMessageFrom(res, "Could not save role"));
    } catch (err) {
      notify.error(err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: "12px", width: "min(40rem,94vw)" } }}>
      <Box sx={{ p: "1.5rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{editing ? "Edit role" : "Create role"}</Box>
          <IconButton aria-label="Close" size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb="1.25rem">
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>Role name *</Box>
            <TextField fullWidth size="small" value={form.name} placeholder="e.g. HR Manager"
              onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setErrors((p) => ({ ...p, name: "" })); }}
              error={!!errors.name} helperText={errors.name} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem" }}>Description *</Box>
            <TextField fullWidth size="small" value={form.description} placeholder="What this role can do"
              onChange={(e) => { setForm((p) => ({ ...p, description: e.target.value })); setErrors((p) => ({ ...p, description: "" })); }}
              error={!!errors.description} helperText={errors.description} />
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mb="0.5rem">
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>Permissions</Box>
          <Box sx={{ fontSize: "0.8125rem", color: perms.size ? tokens.accent : tokens.danger }}>
            {perms.size} selected
          </Box>
        </Stack>
        {errors.perms && <Box sx={{ fontSize: "0.8125rem", color: tokens.danger, mb: "0.5rem" }}>{errors.perms}</Box>}

        <Box sx={{ maxHeight: "22rem", overflowY: "auto", border: `1px solid ${tokens.border}`, borderRadius: "10px" }}>
          {catalogue.map((cat, i) => (
            <Accordion key={cat.topic || i} disableGutters elevation={0} sx={{ "&:before": { display: "none" }, borderBottom: `1px solid ${tokens.border}` }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.text }}>
                  {cat.topic}
                  <Chip size="small" label={(cat.permissions || []).filter((p) => perms.has(p.name)).length || ""}
                    sx={{ ml: 1, height: "1.1rem", fontSize: "0.8125rem", display: (cat.permissions || []).some((p) => perms.has(p.name)) ? "inline-flex" : "none", bgcolor: `${tokens.accentTint}`, color: tokens.accent }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {(cat.permissions || []).map((p) => (
                  <Stack key={p.name} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.5, cursor: "pointer" }} onClick={() => toggle(p.name)}>
                    <Checkbox size="small" checked={perms.has(p.name)} sx={{ p: 0.5 }} />
                    <Box>
                      <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.text }}>{p.name}</Box>
                      {p.description && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{p.description}</Box>}
                    </Box>
                  </Stack>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" mt="1.5rem">
          <BtnSecondary onClick={onClose} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
            {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (editing ? "Save role" : "Create role")}
          </BtnPrimary>
        </Stack>
      </Box>
    </Dialog>
  );
};

const Roles = () => {
  const notify = useNotify();
  const [roles, setRoles] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const [r, c] = await Promise.all([getRoles(), getPermissionCatalogue()]);
      if (r?.success === false) throw new Error(r?.message);
      setRoles(Array.isArray(r?.data) ? r.data : []);
      setCatalogue(Array.isArray(c?.data) ? c.data : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
        <Box>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Roles & Permissions</Box>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Define what people can access, then assign roles from an employee's profile.</Box>
        </Box>
        <BtnPrimary onClick={() => { setEditing(null); setDialogOpen(true); }} sx={{ borderRadius: "8px" }}>
          <AddIcon sx={{ fontSize: "1.1rem", mr: 0.5 }} /> Create Role
        </BtnPrimary>
      </Stack>

      {loading ? <SectionLoader />
        : error ? <EmptyState variant="error" title="Couldn't load roles" onRetry={load} />
        : roles.length === 0 ? <EmptyState title="No roles yet" description="Create your first role to start assigning permissions." />
        : (
          <Stack spacing={1.25}>
            {roles.map((role) => (
              <Stack key={role._id} direction="row" alignItems="center" justifyContent="space-between" sx={{ ...cardSx, p: "1rem 1.25rem" }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                  <Box sx={{ width: "2.5rem", height: "2.5rem", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: `${tokens.accentTint}`, color: tokens.accent, flexShrink: 0 }}>
                    <ShieldOutlinedIcon />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{role.name}</Box>
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                      {role.description} · {(role.permissions || []).length} permission{(role.permissions || []).length === 1 ? "" : "s"}
                    </Box>
                  </Box>
                </Stack>
                <IconButton aria-label="Edit" size="small" onClick={() => { setEditing(role); setDialogOpen(true); }} sx={{ color: tokens.textMuted }}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}

      <RoleDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} catalogue={catalogue} onSaved={load} />
    </Box>
  );
};

export default Roles;
